import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostListener,
  Input,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * Toggle switch. Mirrors design-system.html .toggle.
 *
 * Works with both template-driven and reactive forms via
 * ControlValueAccessor. Optional content is rendered as the label.
 *
 *   <bv-switch [(ngModel)]="enabled">Enable notifications</bv-switch>
 */
@Component({
    selector: "bv-switch",
    templateUrl: "./switch.component.html",
    styleUrls: ["./switch.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BvSwitchComponent),
            multi: true,
        },
    ],
    host: {
        class: "bv-switch",
        role: "switch",
        "[attr.aria-checked]": "checked",
        "[attr.aria-disabled]": "disabled || null",
        "[attr.tabindex]": "disabled ? -1 : 0",
        "[class.bv-switch--checked]": "checked",
        "[class.bv-switch--disabled]": "disabled",
    }
})
export class BvSwitchComponent implements ControlValueAccessor {
  @Input() checked = false;
  @Input() disabled = false;

  private onChange: (v: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  @HostListener("click", ["$event"])
  onClick(event: Event): void {
    this.toggle(event);
  }

  @HostListener("keydown.space", ["$event"])
  @HostListener("keydown.enter", ["$event"])
  onKeydown(event: Event): void {
    this.toggle(event);
  }

  private toggle(event: Event): void {
    if (this.disabled) return;
    event.preventDefault();
    this.checked = !this.checked;
    this.onChange(this.checked);
    this.onTouched();
    this.cdr.markForCheck();
  }

  writeValue(value: unknown): void {
    this.checked = !!value;
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (v: boolean) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }
}
